import { Link } from 'react-router-dom';
import { useState } from 'react';
import Testimonials from './components/Testimonials';
import BookTrialCTA from './components/BookTrialCTA';
import StickyGetStarted from './components/StickyGetStarted';
import NewsletterBanner from './components/NewsletterBanner';
import SEOHead from '../../components/feature/SEOHead';

const homeSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Math Mastery',
    url: import.meta.env.VITE_SITE_URL,
    description: 'Comprehensive K-12 mathematics education and standardized test preparation by NYC\'s Most Outstanding Mathematics Teacher.',
    founder: {
      '@type': 'Person',
      name: 'Augustine Anyimadu-Ahenkae',
      jobTitle: 'Mathematics Teacher and Author',
      award: 'NYC Education Chancellor\'s Most Outstanding Mathematics Teacher',
    },
    offers: {
      '@type': 'Offer',
      category: 'Educational Services',
      availability: 'https://schema.org/InStock',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Augustine Anyimadu-Ahenkae',
    jobTitle: 'Mathematics Teacher & Author',
    worksFor: { '@type': 'EducationalOrganization', name: 'Math Mastery' },
    award: 'NYC Education Chancellor\'s Most Outstanding Mathematics Teacher',
    knowsAbout: ['Mathematics', 'Algebra', 'Geometry', 'Calculus', 'SAT Prep', 'ACT Prep', 'AP Calculus', 'AP Statistics'],
  },
];

export default function HomePage() {
  const [activeTab, setActiveTab] = useState('grades');

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Math Mastery | K-12 Math Education & Test Prep | Mr. Augustine Anyimadu-Ahenkae"
        description="Master mathematics from Kindergarten through Grade 12 and ace the ACT, SAT, AP Calculus, and AP Statistics with NYC's Most Outstanding Mathematics Teacher. Free worksheets, lessons, and comprehensive test prep. 30+ years proven success."
        keywords="math tutoring NYC, K-12 math education, ACT math prep, AP Calculus, AP Statistics, algebra worksheets, geometry, calculus, SAT math, online math courses"
        canonicalPath="/"
        schema={homeSchema}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/ed58018d-bb73-4bb8-8590-7096e85d7af4.png" 
                alt="Math Mastery Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">Math Mastery</span>
            </div>
            <div className="hidden lg:flex items-center gap-8">
              <a href="#grades" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium">Grade Levels</a>
              <Link to="/test-prep" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium whitespace-nowrap">Test Prep</Link>
              <a href="#about" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium">About</a>
              <a href="#resources" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium">Resources</a>
              <Link to="/free-lessons" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium whitespace-nowrap">Free Lessons</Link>
              <Link to="/worksheets" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium whitespace-nowrap">Free Worksheets</Link>
              <Link to="/act-math-worksheets" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium whitespace-nowrap">ACT Math Worksheets</Link>
              <a href="#pricing" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium">Pricing</a>
              <button className="px-5 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap">Login</button>
              <button className="px-6 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap">Sign Up</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2h-4zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6">
              <div className="inline-block px-4 py-2 bg-orange-600 rounded-full text-sm font-medium whitespace-nowrap">
                Honored by NYC Education Chancellor
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Master mathematics at every grade level with New York City's former outstanding high school mathematics teacher.
              </h1>
              <p className="text-xl text-gray-300">
                30+ Years of Proven Success • Thousands of Students Transformed
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <a href="#pricing" className="px-8 py-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-all hover:scale-105 whitespace-nowrap inline-flex items-center gap-2">
                  Start Free Trial
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a href="#grades" className="px-8 py-4 border-2 border-white text-white rounded-lg font-medium hover:bg-white hover:text-slate-900 transition-all whitespace-nowrap">
                  View All Programs
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/251edb121ae1ffd784e6641abf073a59.jpg"
                alt="Students experiencing math success"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Instructor Section */}
      <section id="about" className="py-20 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/edited_1a9eac743fb3fdc6f15dcd305c17bc93_a4781bdd.jpg"
                  alt="Mr. Augustine Anyimadu-Ahenkae"
                  className="rounded-2xl shadow-xl w-full h-auto object-cover transform rotate-1"
                  style={{ objectPosition: 'top' }}
                />
                <div className="absolute -bottom-4 -right-4 bg-orange-600 text-white px-6 py-3 rounded-full font-bold shadow-lg whitespace-nowrap">
                  30+ Years Experience
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-6">
              <div className="text-orange-600 text-sm font-bold tracking-wider uppercase">Your Instructor</div>
              <h2 className="text-4xl font-bold text-gray-900">Mr. Augustine Anyimadu-Ahenkae</h2>
              <p className="text-gray-600 font-medium">Former NYC Public High School Mathematics Teacher</p>
              
              <div className="space-y-4">
                <div className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-award-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">NYC Chancellor's Honor</p>
                    <p className="text-gray-600 text-sm">Most Outstanding Mathematics Teacher</p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-book-3-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Prolific Author</p>
                    <p className="text-gray-600 text-sm">20+ 'Teach Yourself' Mathematics Books</p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-group-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Proven Track Record</p>
                    <p className="text-gray-600 text-sm">Transformed Thousands of Students into Winners</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-xl italic text-gray-700 font-serif">
                  "He can teach Math to even a goat to understand"
                </p>
                <p className="text-sm text-gray-500 mt-2">— Former Student Testimonial</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grade Levels Section */}
      <section id="grades" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-bold mb-4 whitespace-nowrap">
              CHOOSE YOUR PATH
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Select Your Grade Level
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive step-by-step explanations aligned with Common Core and state standards
            </p>
          </div>

          {/* Elementary Grades */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Elementary School (Grades K–4)</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Kindergarten */}
              <Link to="/grade/k" className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-orange-200 block">
                <div className="text-5xl font-thin text-orange-600 mb-2">K</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Kindergarten</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i><span>Counting &amp; Cardinality</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i><span>Addition &amp; Subtraction</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i><span>Shapes &amp; Measurement</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Kindergarten
                </div>
              </Link>

              {/* Grade 1 */}
              <Link to="/grade/1" className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-teal-200 block">
                <div className="text-6xl font-thin text-teal-600 mb-2">1</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 1</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Word Problems</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Place Value to 99</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Time &amp; Measurement</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 1
                </div>
              </Link>

              {/* Grade 2 */}
              <Link to="/grade/2" className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-rose-200 block">
                <div className="text-6xl font-thin text-rose-600 mb-2">2</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 2</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-rose-600 mt-0.5"></i><span>Place Value to 1000</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-rose-600 mt-0.5"></i><span>Money &amp; Time</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-rose-600 mt-0.5"></i><span>Arrays &amp; Fractions</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-rose-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 2
                </div>
              </Link>

              {/* Grade 3 */}
              <Link to="/grade/3" className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-amber-200 block">
                <div className="text-6xl font-thin text-amber-600 mb-2">3</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 3</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>Multiplication &amp; Division</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>Fractions &amp; Area</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>State Test Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 3
                </div>
              </Link>

              {/* Grade 4 */}
              <Link to="/grade/4" className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-green-200 block">
                <div className="text-6xl font-thin text-green-600 mb-2">4</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 4</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i><span>Multi-Digit Multiplication</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i><span>Fractions &amp; Angles</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i><span>State Test Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 4
                </div>
              </Link>

            </div>
          </div>

          {/* Middle School Grades */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Middle School (Grades 5–8)</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

              {/* Grade 5 */}
              <Link to="/grade/5" className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-teal-200 block">
                <div className="text-6xl font-thin text-teal-600 mb-2">5</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 5</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Decimals &amp; Fractions</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Order of Operations</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>State Test Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 5
                </div>
              </Link>

              {/* Grade 6 */}
              <Link to="/grade/6" className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-indigo-200 block">
                <div className="text-6xl font-thin text-indigo-600 mb-2">6</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 6</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-indigo-600 mt-0.5"></i><span>Ratios &amp; Percents</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-indigo-600 mt-0.5"></i><span>Integers &amp; Equations</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-indigo-600 mt-0.5"></i><span>State Test Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 6
                </div>
              </Link>

              {/* Grade 7 - Now Active */}
              <Link to="/grade/7" className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-amber-200 block">
                <div className="text-6xl font-thin text-amber-600 mb-2">7</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 7</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>Proportional Relationships</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>Geometry &amp; Statistics</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-amber-600 mt-0.5"></i><span>State Test Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 7
                </div>
              </Link>

              {/* Grade 8 - Now Active */}
              <Link to="/grade/8" className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-teal-200 block">
                <div className="text-6xl font-thin text-teal-600 mb-2">8</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Grade 8</h4>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Linear Equations</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>Functions &amp; Geometry</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i><span>HSPT / ISEE / SHSAT Prep</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 8
                </div>
              </Link>

            </div>
          </div>

          {/* High School Grades */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">High School (Grades 9-12)</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Grade 9 - Algebra 1 - Now Active */}
              <Link to="/grade/9" className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer block">
                <div className="text-6xl font-thin mb-2">9</div>
                <h4 className="text-xl font-bold mb-3">Grade 9</h4>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5"></i>
                    <span>Real Number System</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5"></i>
                    <span>Linear Equations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5"></i>
                    <span>Quadratic Equations</span>
                  </li>
                </ul>
                <div className="w-full px-4 py-2 bg-white text-orange-600 rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 9
                </div>
              </Link>

              {/* Grade 10 - Geometry - Now Active */}
              <Link to="/geometry" className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer block">
                <div className="text-6xl font-thin mb-2">10</div>
                <h4 className="text-xl font-bold mb-3">Grade 10</h4>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Geometry</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Triangle Congruence & Similarity</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Trigonometry Intro</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-white text-orange-600 rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 10
                </div>
              </Link>

              {/* Grade 11 - Algebra 2 - Now Active */}
              <Link to="/grade/11" className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer block">
                <div className="text-6xl font-thin mb-2">11</div>
                <h4 className="text-xl font-bold mb-3">Grade 11</h4>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Polynomials & Rational Functions</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Exponential & Logarithmic Functions</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Trigonometry & Unit Circle</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-white text-orange-600 rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 11
                </div>
              </Link>

              {/* Grade 12 - Pre-Calculus - Now Active */}
              <Link to="/precalc" className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer block">
                <div className="text-6xl font-thin mb-2">12</div>
                <h4 className="text-xl font-bold mb-3">Grade 12</h4>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Advanced Functions & Analysis</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Vectors & Matrices</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill mt-0.5"></i><span>Introduction to Limits</span></li>
                </ul>
                <div className="w-full px-4 py-2 bg-white text-orange-600 rounded-lg font-medium text-sm text-center whitespace-nowrap">
                  Explore Grade 12
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Standardized Tests Section */}
      <section id="tests" className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 whitespace-nowrap">
              <i className="ri-trophy-line"></i>
              <span>ACE YOUR STANDARDIZED TESTS</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Test Prep That Gets Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive preparation for ACT, SAT, and state standardized tests with proven strategies and practice materials
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=confident%20diverse%20students%20taking%20standardized%20test%20in%20bright%20modern%20classroom%20focused%20expressions%20test%20booklets%20pencils%20organized%20testing%20environment%20success%20achievement&width=700&height=600&seq=test-prep-banner&orientation=landscape"
                alt="Students taking standardized tests"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                style={{ objectPosition: 'top' }}
              />
              <div className="absolute -bottom-4 -right-4 bg-violet-600 text-white px-6 py-3 rounded-full font-bold shadow-lg whitespace-nowrap">
                3,250+ Practice Questions
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900">
                Master Every Section with Expert Guidance
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive test prep courses cover all sections of major standardized tests, with detailed explanations, proven strategies, and unlimited practice.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-violet-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Test-Aligned Content</div>
                    <div className="text-gray-600 text-sm">Every lesson matches actual test formats and standards</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Proven Strategies</div>
                    <div className="text-gray-600 text-sm">Learn time-saving techniques and test-taking tips</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-indigo-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Full Practice Tests</div>
                    <div className="text-gray-600 text-sm">Simulate real testing conditions with timed exams</div>
                  </div>
                </li>
              </ul>
              <Link 
                to="/test-prep"
                className="inline-flex items-center gap-2 px-8 py-4 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 transition-all hover:scale-105 whitespace-nowrap"
              >
                Explore Test Prep Courses <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/test-prep/act/math" className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-violet-200 hover:border-violet-400">
              <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-2xl text-violet-700"></i>
              </div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-bold mb-3 whitespace-nowrap">
                <i className="ri-check-line"></i>
                <span>Available Now</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">ACT Math</h3>
              <p className="text-gray-600 mb-4 text-sm">Complete curriculum covering all 6 domains with 600+ practice questions</p>
              <div className="flex items-center gap-2 text-violet-700 font-semibold whitespace-nowrap">
                <span>Start Learning</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 opacity-75">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-gray-500"></i>
              </div>
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-bold mb-3 whitespace-nowrap">
                <i className="ri-time-line"></i>
                <span>Coming Soon</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">ACT English</h3>
              <p className="text-gray-600 mb-4 text-sm">Grammar, punctuation, and rhetorical skills preparation</p>
              <div className="flex items-center gap-2 text-gray-500 font-semibold whitespace-nowrap">
                <span>Available Soon</span>
                <i className="ri-lock-line"></i>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 opacity-75">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-flask-line text-2xl text-gray-500"></i>
              </div>
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-bold mb-3 whitespace-nowrap">
                <i className="ri-time-line"></i>
                <span>Coming Soon</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">ACT Science</h3>
              <p className="text-gray-600 mb-4 text-sm">Data interpretation and scientific investigation skills</p>
              <div className="flex items-center gap-2 text-gray-500 font-semibold whitespace-nowrap">
                <span>Available Soon</span>
                <i className="ri-lock-line"></i>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/test-prep"
              className="inline-flex items-center gap-2 text-violet-700 font-semibold text-lg hover:gap-3 transition-all whitespace-nowrap"
            >
              <span>View All Test Prep Courses</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Flexible Membership Options</h2>
            <p className="text-xl text-gray-600">Choose the plan that fits your learning journey</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Trial */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="text-sm font-bold text-gray-600 mb-2 whitespace-nowrap">Free Trial</div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600"> for 14 days</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Access to 1 grade level</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Limited video lessons</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">5 practice tests</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Basic progress tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Email support</span>
                </li>
              </ul>
              <button className="w-full px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-lg font-medium hover:bg-gray-900 hover:text-white transition-all text-sm whitespace-nowrap">
                Start Free Trial
              </button>
            </div>

            {/* Most Popular */}
            <div className="bg-white border-4 border-orange-600 rounded-2xl p-8 relative transform scale-105 shadow-2xl">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                Most Popular
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2 whitespace-nowrap">Standard Plan</div>
              <div className="mb-2">
                <span className="text-5xl font-bold text-gray-900">$29.99</span>
                <span className="text-gray-600">/month</span>
              </div>
              <div className="text-sm text-gray-600 mb-6">or $299/year (Save 17%)</div>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">All grade levels (1-12)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Unlimited video lessons</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Unlimited practice tests</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">2 standardized test preps</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Advanced progress tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Downloadable worksheets</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Priority email support</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Monthly live Q&A sessions</span>
                </li>
              </ul>
              <button className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-all text-sm whitespace-nowrap">
                Get Started Now <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Premium */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="text-sm font-bold text-gray-600 mb-2 whitespace-nowrap">All Access</div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$49.99</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Everything in Standard</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">All standardized test preps</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">1-on-1 tutoring (2 hrs/month)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Personalized study plans</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Direct access to Mr. Augustine</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">All digital books included</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Weekly live classes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Certificate of completion</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">Lifetime access to materials</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-lg mt-0.5"></i>
                  <span className="text-gray-700">24/7 priority support</span>
                </li>
              </ul>
              <button className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all text-sm whitespace-nowrap">
                Choose Premium
              </button>
            </div>
          </div>

          <div className="text-center mt-8 flex items-center justify-center gap-2 text-gray-600">
            <i className="ri-shield-check-line text-green-600"></i>
            <span className="text-sm">All plans include 30-day money-back guarantee</span>
          </div>
        </div>
      </section>

      {/* Free Lessons Banner */}
      <div className="mb-12 bg-gradient-to-r from-orange-600 to-red-500 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
            <i className="ri-gift-line text-3xl"></i>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white/80 mb-1">100% Free — No Login Required</div>
            <h3 className="text-2xl font-bold">Free Lessons with Full Explanations</h3>
            <p className="text-white/80 text-sm mt-1">Textbook theory + Mr. Augustine's exclusive short methods + 20 practice questions per topic</p>
          </div>
        </div>
        <Link to="/free-lessons" className="px-8 py-4 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2">
          Start Free Lessons <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {/* Free Worksheets Banner */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-teal-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=diverse%20students%20working%20on%20colorful%20math%20worksheets%20at%20desk%20with%20pencils%20and%20calculators%20bright%20educational%20materials%20organized%20learning%20environment%20natural%20lighting%20happy%20focused%20expressions&width=700&height=600&seq=worksheets-banner&orientation=landscape"
                alt="Students working on worksheets"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                style={{ objectPosition: 'top' }}
              />
              <div className="absolute -bottom-4 -right-4 bg-teal-600 text-white px-6 py-3 rounded-full font-bold shadow-lg whitespace-nowrap">
                150+ Free Worksheets
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-teal-600 text-white rounded-full text-sm font-bold whitespace-nowrap">
                FREE DOWNLOADABLE RESOURCES
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                Practice Makes Perfect with Our Free Worksheets
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Download professionally designed worksheets covering every grade level from Kindergarten through Pre-Calculus, plus comprehensive test prep materials.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-gray-900">All Grade Levels</div>
                    <div className="text-gray-600 text-sm">K-12 worksheets aligned with Common Core standards</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-gray-900">Test Prep Materials</div>
                    <div className="text-gray-600 text-sm">SAT, ACT, State Tests, SHSAT, and more</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-gray-900">Instant Download</div>
                    <div className="text-gray-600 text-sm">Print and practice immediately, no signup required</div>
                  </div>
                </li>
              </ul>
              <Link to="/worksheets" className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap">
                Browse Free Worksheets <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Free Resources Section */}
      <section id="resources" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Free Learning Resources</h2>
            <div className="flex gap-3">
              <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer">
                <option>All Topics</option>
                <option>Algebra</option>
                <option>Geometry</option>
                <option>Calculus</option>
              </select>
              <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer">
                <option>All Grades</option>
                <option>Elementary</option>
                <option>Middle School</option>
                <option>High School</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { type: 'video', title: 'Introduction to Fractions', duration: '12:45', category: 'free', thumb: 'elementary student learning fractions with visual pie charts colorful educational illustration simple background' },
              { type: 'pdf', title: 'Algebra 1 Formula Sheet', category: 'free', thumb: 'algebra formulas and equations on clean white paper educational reference sheet organized layout' },
              { type: 'article', title: 'SAT Math Tips & Strategies', category: 'member', thumb: 'SAT test preparation study materials with pencils and calculator on desk organized workspace' },
              { type: 'video', title: 'Geometry Basics Explained', duration: '18:30', category: 'free', thumb: 'geometric shapes triangles circles squares colorful educational diagram simple clean background' },
              { type: 'pdf', title: 'Times Tables Practice', category: 'free', thumb: 'multiplication table chart colorful numbers grid educational poster for elementary students' },
              { type: 'article', title: 'Calculus Made Simple', category: 'member', thumb: 'calculus graphs and derivatives on whiteboard mathematical curves educational illustration' },
              { type: 'video', title: 'Word Problems Mastery', duration: '15:20', category: 'free', thumb: 'student solving word problems in notebook with pencil focused learning environment bright lighting' },
              { type: 'pdf', title: 'GRE Math Practice Test', category: 'member', thumb: 'GRE test booklet with answer sheet standardized test materials professional setting' }
            ].map((resource, idx) => (
              <div key={idx} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border-l-4" style={{ borderLeftColor: resource.category === 'free' ? '#FF6B35' : '#3B82F6' }}>
                <div className="relative h-48">
                  <img 
                    src={`https://readdy.ai/api/search-image?query=$%7Bresource.thumb%7D&width=400&height=300&seq=resource-${idx}&orientation=landscape`}
                    alt={resource.title}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'top' }}
                  />
                  {resource.type === 'video' && (
                    <>
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full">
                          <i className="ri-play-fill text-3xl text-orange-600"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs font-medium whitespace-nowrap">
                        {resource.duration}
                      </div>
                    </>
                  )}
                  {resource.type === 'pdf' && (
                    <div className="absolute top-2 left-2 w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                      <i className="ri-file-pdf-line text-xl text-white"></i>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-gray-900 mb-2 text-sm">{resource.title}</h4>
                  <button className="text-orange-600 font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap">
                    {resource.type === 'video' ? 'Watch Now' : resource.type === 'pdf' ? 'Download' : 'Read More'}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Book a Free Trial Lesson CTA */}
      <BookTrialCTA />

      {/* Newsletter Subscription Banner */}
      <NewsletterBanner />

      {/* Sticky Get Started Banner */}
      <StickyGetStarted />

      {/* Books Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Helpful Books by the Author</h2>
            <p className="text-xl text-gray-600">Comprehensive "Teach Yourself" mathematics books for every level</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Teach Yourself Algebra 1', desc: 'Complete guide to mastering Algebra 1 concepts', level: 'High School' },
              { title: 'Teach Yourself Geometry', desc: 'Visual approach to geometric principles', level: 'High School' },
              { title: 'Teach Yourself SAT Math', desc: 'Proven strategies for SAT success', level: 'Test Prep' },
              { title: 'Teach Yourself Calculus', desc: 'From basics to advanced calculus topics', level: 'Advanced' },
              { title: 'Teach Yourself Grade 5 Math', desc: 'Building strong foundations', level: 'Elementary' },
              { title: 'Teach Yourself Trigonometry', desc: 'Master trig functions and applications', level: 'High School' },
              { title: 'Teach Yourself GRE Math', desc: 'Graduate school test preparation', level: 'Test Prep' },
              { title: 'Teach Yourself Precalculus', desc: 'Bridge to advanced mathematics', level: 'Advanced' }
            ].map((book, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer border border-gray-200">
                <div className="w-full h-48 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg mb-4 flex items-center justify-center">
                  <i className="ri-book-2-line text-6xl text-white"></i>
                </div>
                <div className="text-xs font-bold text-orange-600 mb-2 uppercase whitespace-nowrap">{book.level}</div>
                <h4 className="font-bold text-gray-900 mb-2">{book.title}</h4>
                <p className="text-sm text-gray-600 mb-4">{book.desc}</p>
                <button className="text-orange-600 font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap">
                  View Book <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Books Coming Soon</h3>
            <p className="text-xl mb-6 max-w-2xl mx-auto">
              More comprehensive guides covering AP Statistics, AP Calculus, CLEP exams, and advanced mathematics topics
            </p>
            <button className="px-8 py-3 bg-white text-orange-600 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap">
              Get Notified
            </button>
          </div>
        </div>
      </section>

      {/* Tutoring Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=one%20on%20one%20tutoring%20session%20teacher%20helping%20student%20with%20mathematics%20personalized%20learning%20warm%20encouraging%20atmosphere%20bright%20modern%20study%20room&width=700&height=600&seq=tutoring-session&orientation=landscape"
                alt="One-on-one tutoring"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                style={{ objectPosition: 'top' }}
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-orange-600 rounded-full text-sm font-bold whitespace-nowrap">
                PERSONALIZED LEARNING
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold">
                One-on-One Tutoring with<br />
                <span className="text-orange-400">GREAT SHAKESPEARO</span>
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                Get personalized attention and customized learning plans tailored to your specific needs. Work directly with experienced tutors trained in Mr. Augustine's proven methods.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Flexible Scheduling</div>
                    <div className="text-gray-400 text-sm">Book sessions at times that work for you</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Customized Learning Plans</div>
                    <div className="text-gray-400 text-sm">Tailored to your goals and learning style</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-600 rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-lg">Proven Methods</div>
                    <div className="text-gray-400 text-sm">Using Mr. Augustine's award-winning techniques</div>
                  </div>
                </li>
              </ul>
              <button className="px-8 py-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-all hover:scale-105 inline-flex items-center gap-2 whitespace-nowrap">
                Schedule a Session
                <i className="ri-calendar-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            <div>
              <h4 className="font-bold mb-4 text-orange-400">Elementary</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/grade/k" className="text-gray-400 hover:text-orange-400 transition-colors">Kindergarten</Link>
                </li>
                {[1, 2, 3, 4].map(grade => (
                  <li key={grade}>
                    <Link to={`/grade/${grade}`} className="text-gray-400 hover:text-orange-400 transition-colors">Grade {grade}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-orange-400">Middle &amp; High School</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/grade/5" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 5</Link>
                </li>
                <li>
                  <Link to="/grade/6" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 6</Link>
                </li>
                <li>
                  <Link to="/grade/7" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 7</Link>
                </li>
                <li>
                  <Link to="/grade/8" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 8</Link>
                </li>
                <li>
                  <Link to="/grade/9" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 9</Link>
                </li>
                <li>
                  <Link to="/geometry" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 10 - Geometry</Link>
                </li>
                <li>
                  <Link to="/grade/11" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 11</Link>
                </li>
                <li>
                  <Link to="/precalc" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 12 - Pre-Calculus</Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-orange-400">Test Prep</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/test-prep" className="text-gray-400 hover:text-orange-400 transition-colors">All Test Prep</Link></li>
                <li><Link to="/test-prep/act/math" className="text-gray-400 hover:text-orange-400 transition-colors">ACT Math</Link></li>
                <li><Link to="/test-prep" className="text-gray-400 hover:text-orange-400 transition-colors">ACT English</Link></li>
                <li><Link to="/test-prep" className="text-gray-400 hover:text-orange-400 transition-colors">ACT Science</Link></li>
                <li><Link to="/test-prep" className="text-gray-400 hover:text-orange-400 transition-colors">SAT Prep</Link></li>
                <li><Link to="/test-prep" className="text-gray-400 hover:text-orange-400 transition-colors">State Tests</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-orange-400">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#resources" className="text-gray-400 hover:text-orange-400 transition-colors">Free Videos</a></li>
                <li><a href="#resources" className="text-gray-400 hover:text-orange-400 transition-colors">Practice Tests</a></li>
                <li><Link to="/worksheets" className="text-gray-400 hover:text-orange-400 transition-colors">Free Worksheets</Link></li>
                <li><Link to="/act-math-worksheets" className="text-gray-400 hover:text-orange-400 transition-colors">ACT Math Worksheets</Link></li>
                <li><Link to="/grade-9-worksheets" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 9 Worksheets</Link></li>
                <li><Link to="/grade-9-study-plan" className="text-gray-400 hover:text-orange-400 transition-colors">Grade 9 Study Plan</Link></li>
                <li><Link to="/free-lessons" className="text-gray-400 hover:text-orange-400 transition-colors">Free Lessons</Link></li>
                <li><a href="#about" className="text-gray-400 hover:text-orange-400 transition-colors">About</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-orange-400 transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-orange-400">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="text-gray-400 hover:text-orange-400 transition-colors">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">FAQ</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="grid md:grid-cols-3 gap-8 items-center mb-8">
              <div className="flex items-center gap-3">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ed58018d-bb73-4bb8-8590-7096e85d7af4.png" 
                  alt="Math Mastery" 
                  className="h-16 w-16 rounded-lg object-contain"
                />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold font-serif">
                  Transform Your<br />
                  Mathematical<br />
                  Journey Today
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-3 text-sm">Stay Updated</h4>
                <p className="text-gray-400 text-sm mb-3">Get free tips and resources</p>
                <div className="flex gap-2">
                  <input 
                    type="email" 
                    placeholder="Your email" 
                    className="flex-1 px-4 py-2 bg-slate-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-orange-600"
                  />
                  <button className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors text-sm whitespace-nowrap">
                    Subscribe <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>© 2025 Augustine Anyimadu-Ahenkae. All rights reserved.</p>
              <div className="flex items-center gap-6">
                <a href="#" className="hover:text-orange-400 transition-colors">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="hover:text-orange-400 transition-colors">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a href="#" className="hover:text-orange-400 transition-colors">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="hover:text-orange-400 transition-colors">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}